import api from './api'
import type {
  FormerEmployee,
  FormerEmployeeFile,
  FormerEmployeeFilters,
  FormerEmployeeResponse,
} from '@/types/formerEmployee'
import type { EmployeeDocument } from '@/types/employeeDocument'

interface RecordResponse {
  success: boolean
  message: string
  data: FormerEmployee
}

interface FilesResponse {
  success: boolean
  message: string
  data: FormerEmployeeFile[]
}

export const formerEmployeeService = {
  async getFormerEmployees(
    params?: FormerEmployeeFilters & { page?: number; per_page?: number }
  ): Promise<FormerEmployeeResponse> {
    const response = await api.get<FormerEmployeeResponse>('/former-employees', { params })
    return response.data
  },

  async getFormerEmployee(id: number): Promise<FormerEmployee> {
    const response = await api.get<RecordResponse>(`/former-employees/${id}`)
    return response.data.data
  },

  async createFormerEmployee(formData: FormData): Promise<FormerEmployee> {
    const response = await api.post<RecordResponse>('/former-employees', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data.data
  },

  async updateFormerEmployee(id: number, data: Record<string, unknown>): Promise<FormerEmployee> {
    const response = await api.put<RecordResponse>(`/former-employees/${id}`, data)
    return response.data.data
  },

  async deleteFormerEmployee(id: number): Promise<void> {
    await api.delete(`/former-employees/${id}`)
  },

  async uploadFiles(id: number, formData: FormData): Promise<FormerEmployeeFile[]> {
    const response = await api.post<FilesResponse>(`/former-employees/${id}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data.data
  },

  async downloadFile(formerEmployeeId: number, fileId: number, filename?: string): Promise<void> {
    const response = await api.get(`/former-employees/${formerEmployeeId}/files/${fileId}/download`, {
      responseType: 'blob',
    })
    const blob = new Blob([response.data], {
      type: response.headers['content-type'] || 'application/octet-stream',
    })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename || `former_employee_file_${fileId}`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  },

  async deleteFile(formerEmployeeId: number, fileId: number): Promise<void> {
    await api.delete(`/former-employees/${formerEmployeeId}/files/${fileId}`)
  },

  async restoreFile(formerEmployeeId: number, fileId: number): Promise<EmployeeDocument> {
    const response = await api.post<{ success: boolean; message: string; data: EmployeeDocument }>(
      `/former-employees/${formerEmployeeId}/files/${fileId}/restore`
    )
    return response.data.data
  },
}

export default formerEmployeeService
